import React, { createContext, useContext, ReactNode } from 'react';
import { usePlayerStore } from '../stores/playerStore';
import type { RadioStation } from '../types/radio';

interface PlayerContextType {
  currentStation: RadioStation | null;
  isPlaying: boolean;
  volume: number;
  isFullscreen: boolean;
  sleepTimer: number | null;
  isConnected: boolean;
  isBuffering: boolean;
  connectionQuality: 'good' | 'fair' | 'poor';
  hasUserInteracted: boolean;
  setCurrentStation: (station: RadioStation | null) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setVolume: (volume: number) => void;
  setIsFullscreen: (isFullscreen: boolean) => void;
  resetPlayer: () => void;
  restoreLastSession: () => void;
  setSleepTimer: (minutes: number | null) => void;
  setIsConnected: (isConnected: boolean) => void;
  setIsBuffering: (isBuffering: boolean) => void;
  setConnectionQuality: (quality: 'good' | 'fair' | 'poor') => void;
  setHasUserInteracted: (hasInteracted: boolean) => void;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const PlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const {
    currentStation,
    isPlaying,
    volume,
    isFullscreen,
    sleepTimer,
    isConnected,
    isBuffering,
    connectionQuality,
    hasUserInteracted,
    setCurrentStation,
    setIsPlaying,
    setVolume,
    setIsFullscreen,
    resetPlayer,
    restoreLastSession,
    setSleepTimer,
    setIsConnected,
    setIsBuffering,
    setConnectionQuality,
    setHasUserInteracted,
  } = usePlayerStore();

  return (
    <PlayerContext.Provider
      value={{
        currentStation,
        isPlaying,
        volume,
        isFullscreen,
        sleepTimer,
        isConnected,
        isBuffering,
        connectionQuality,
        hasUserInteracted,
        setCurrentStation,
        setIsPlaying,
        setVolume,
        setIsFullscreen,
        resetPlayer,
        restoreLastSession,
        setSleepTimer,
        setIsConnected,
        setIsBuffering,
        setConnectionQuality,
        setHasUserInteracted,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}; 